// PURE idle animations of the board (M10; UX §5 "MachineCycle", "ChuteTransfer", "StallChanged"), as closed
// functions of the FX clock: chute chevrons scroll on ONE global phase clock, error stall badges pulse at
// stallPulseHz (opacity only — also under reduce motion, UX §5 "Pulses animate opacity only"), a working
// drill spins its bit (two frames), a running Smelter's mouth glows at smelterGlowHz, a running Grinder
// turns its wheel. Reduce motion: sprite swaps only (the bit stays, the glow is steady, the wheel stops).
// Zero-duration mode (?test): everything rests (phase 0, full alpha, no spin).
import type { SimDefs } from '../../sim/defs.ts';
import { progressPermille, shownStall } from '../../sim/query.ts';
import type { Building } from '../../sim/state.ts';

export interface IdleMode {
  /** ?test zero-duration mode: nothing idles. */
  still: boolean;
  /** Reduce motion: no spin / scroll-free glow (sprite swaps only). */
  rm: boolean;
}

/** The chute chevron phase at clock time `t` (one global clock; 0 when still). */
export function chevronPhase(t: number, cycleMs: number, phases: number, still: boolean): number {
  if (still || cycleMs <= 0 || phases <= 0) return 0;
  return Math.floor(t / (cycleMs / phases)) % phases;
}

/** Opacity of an error stall badge (a cosine pulse between `min` and 1; 1 when still). */
export function stallPulse(t: number, hz: number, min: number, still: boolean): number {
  if (still) return 1;
  const wave = 0.5 + 0.5 * Math.cos(2 * Math.PI * hz * (t / 1000));
  return min + (1 - min) * wave;
}

/** A machine is running its cycle (not blocked by a full output, not paused, not a ghost). */
export function running(d: SimDefs, b: Building): boolean {
  if (b.paused || b.ghost !== null) return false;
  const p = progressPermille(d, b);
  return p !== null && p < 1000;
}

/** A drill is working (a deposit ahead, not paused, not a ghost). */
export function drilling(d: SimDefs, b: Building): boolean {
  return b.type === 'drill' && !b.paused && b.ghost === null && shownStall(d, b) === null;
}

/** The drill bit frame (0 / 1): alternates every `spinMs` while working (0 when resting / still / RM). */
export function bitFrame(t: number, spinMs: number, working: boolean, m: IdleMode): number {
  if (!working || m.still || m.rm || spinMs <= 0) return 0;
  return Math.floor(t / spinMs) % 2;
}

/** The Smelter mouth glow alpha: a pulse between min and max while running (RM: steady max), else 0. */
export function glowAlpha(
  t: number,
  hz: number,
  min: number,
  max: number,
  working: boolean,
  m: IdleMode,
): number {
  if (!working) return 0;
  if (m.still || m.rm) return max;
  const wave = 0.5 + 0.5 * Math.sin(2 * Math.PI * hz * (t / 1000));
  return min + (max - min) * wave;
}

/** The Grinder wheel's angle at idle time `t`: it turns while running (at rest — 0° — when stopped / still / RM). */
export function wheelAngle(t: number, degPerSec: number, working: boolean, m: IdleMode): number {
  if (!working || m.still || m.rm) return 0;
  return ((degPerSec * t) / 1000) % 360;
}
